import { useMemo, useState } from "react"
import { FiPlus, FiTrash2 } from "react-icons/fi"

import { Title, Text } from "components/Form"
import { limsResults, limsSpecs } from "state_management"
import useT from "utils/hooks/useT"

// Preview (PRD-006 K23) — a live, throw-away render of the analyst's result-entry screen for the
// spec currently being edited. It shows ONE sample card EXACTLY as the analyst will see it at
// result entry (ResultEntryPanel): measured parameters get a value field with the criterion hint;
// calculated parameters get one field per declared input (an aggregate input gets the add/remove
// list editor), with the engine's live computed value + per-parameter Pass/Fail and an overall
// judgement badge. Inputs sourced from a reference standard ("standardPurity"/"standardWaterContent")
// are shown disabled with a note — at real entry they are auto-filled from the run's consumables,
// which this preview has no access to (and deliberately does not fetch).
//
// It is PURELY LOCAL: nothing is dispatched or persisted. It reuses the SAME judging logic the
// real panel and the server's recompute share (limsResults.judgeValue / judgeCalculatedParameter /
// computeOverall, via the calcEngine) — no engine logic is duplicated here. The spec parameters are
// run through limsSpecs.normalizeParameters first so the preview judges against the exact shape the
// result-entry flow will (blank-key inputs dropped, measured/calculated fields coerced) — i.e. the
// preview reflects what WILL be persisted, not the in-flight editor draft with half-typed keys.
//
// Props: parameters (the live editor parameter list).
export default function Preview({ parameters }) {
  const { t } = useT("common")

  // Judge against the normalised parameters (the shape result entry + the server will use).
  const normalized = useMemo(() => limsSpecs.normalizeParameters(parameters), [parameters])

  // Local entry state for the single preview sample:
  //   { values: { [parameterId]: rawString }, inputs: { [parameterId]: { [inputKey]: string | string[] } } }
  const [values, setValues] = useState({})
  const [inputs, setInputs] = useState({})

  const setValue = (parameterId, raw) => setValues((prev) => ({ ...prev, [parameterId]: raw }))
  const setInput = (parameterId, inputKey, raw) =>
    setInputs((prev) => ({ ...prev, [parameterId]: { ...(prev[parameterId] || {}), [inputKey]: raw } }))
  const setAggregate = (parameterId, inputKey, list) =>
    setInputs((prev) => ({ ...prev, [parameterId]: { ...(prev[parameterId] || {}), [inputKey]: list } }))

  // Per-parameter live judgement, mirroring ResultEntryPanel.judgeParam. Measured parameters judge
  // the entered value; calculated parameters run the engine over the entered inputs. The spec
  // context ({ parameters, entries }) lets the engine resolve cross-parameter references
  // (SPEC-023) from the preview's OWN entered values — typing a source value re-judges the chain.
  const judgeParam = (p) => {
    if (limsSpecs.isCalculatedParameter(p)) {
      return limsResults.judgeCalculatedParameter(p, inputs[p.id] || {}, {
        parameters: normalized,
        entries: { values, inputs },
      })
    }
    return { pass: limsResults.judgeValue(p, values[p.id]), computedValue: null, rounded: null, error: null }
  }
  const overall = limsResults.computeOverall(normalized.map((p) => ({ pass: judgeParam(p).pass })))

  // SPEC-023: the display name of a referenced source parameter (for the "from parameter X"
  // note on a read-only parameter-bound input).
  const sourceNameOf = (parameterId) => {
    const source = normalized.find((p) => p.id === parameterId)
    return source ? source.name || t("limsSpecPreviewUnnamed") : "?"
  }

  return (
    <div className="SpecEditor-preview">
      <Title>{t("limsSpecPreviewTitle")}</Title>
      <p className="SpecEditor-preview-help">{t("limsSpecPreviewHelp")}</p>

      {normalized.length === 0 ? (
        <p className="SpecEditor-empty">{t("limsSpecPreviewNoParameters")}</p>
      ) : (
        <section className="SpecEditor-preview-sample">
          <div className="SpecEditor-preview-sample-head">
            <span className="SpecEditor-preview-sample-name">{t("limsSpecPreviewSampleName")}</span>
            <span className="SpecEditor-preview-judge">
              <PassFlag pass={overall === null ? null : overall === "PASS"} t={t} />
            </span>
          </div>

          <table className="SpecEditor-preview-grid">
            <tbody>
              {normalized.map((p) =>
                limsSpecs.isCalculatedParameter(p) ? (
                  <CalculatedInputRows
                    key={p.id}
                    param={p}
                    inputValues={inputs[p.id] || {}}
                    judged={judgeParam(p)}
                    sourceNameOf={sourceNameOf}
                    onScalarChange={(key, v) => setInput(p.id, key, v)}
                    onAggregateChange={(key, list) => setAggregate(p.id, key, list)}
                    t={t}
                  />
                ) : (
                  <tr key={p.id}>
                    <th className="SpecEditor-preview-label">
                      {p.name || t("limsSpecPreviewUnnamed")}
                      {p.unit ? ` (${p.unit})` : ""}
                      <span className="SpecEditor-preview-type"> · {t("limsSpecKindMeasured")}</span>
                      <div className="SpecEditor-preview-criterion">{criterionText(p, t)}</div>
                    </th>
                    <td className="SpecEditor-preview-value">
                      <Text
                        value={values[p.id] === undefined || values[p.id] === null ? "" : String(values[p.id])}
                        onChange={(v) => setValue(p.id, v)}
                      />
                    </td>
                    <td className="SpecEditor-preview-flag">
                      <PassFlag pass={judgeParam(p).pass} t={t} />
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </section>
      )}
    </div>
  )
}

// The aggregate input key for a calculated parameter (the input entered as an n-value list), or null.
function aggregateKeyOf(param) {
  return param && param.aggregate && param.aggregate.inputKey ? param.aggregate.inputKey : null
}

// The calculated-parameter rows for the preview sample. The sub-header row carries the parameter
// name + type + criterion AND the engine's live computed value + Pass/Fail (RR5 P3 — the result
// sits on the parameter row itself, not a separate "Computed" row), followed by one label|value
// row per declared input (an aggregate input is a small add/remove list editor; a
// reference-standard-sourced input is disabled with an "auto-filled" note — the preview has no run
// consumables to fill from; a parameter-sourced input is read-only with a "from parameter X" note
// and shows the value RESOLVED from the referenced parameter's preview entry — SPEC-023).
// Mirrors ResultEntryPanel.CalculatedInputRows (kept in sync — see RR5 P2/P3/P4).
function CalculatedInputRows({ param, inputValues, judged, sourceNameOf, onScalarChange, onAggregateChange, t }) {
  const aggregateKey = aggregateKeyOf(param)
  const declared = param.inputs || []
  const computed = judged && judged.rounded
  const error = judged && judged.error
  return (
    <>
      <tr className="SpecEditor-preview-subhead">
        <th className="SpecEditor-preview-label">
          {param.name || t("limsSpecPreviewUnnamed")}
          {param.unit ? ` (${param.unit})` : ""}
          <span className="SpecEditor-preview-type"> · {t("limsSpecKindCalculated")}</span>
          <div className="SpecEditor-preview-criterion">{criterionText(param, t)}</div>
        </th>
        <td className="SpecEditor-preview-value">
          {error ? (
            <span className="SpecEditor-preview-calc-error" title={error}>
              {t("limsResultCalcError")}
            </span>
          ) : (
            <span className="SpecEditor-preview-calc-value">
              {computed === null || computed === undefined ? "—" : String(computed)}
              {param.unit ? ` ${param.unit}` : ""}
            </span>
          )}
        </td>
        <td className="SpecEditor-preview-flag">
          <PassFlag pass={judged ? judged.pass : null} t={t} />
        </td>
      </tr>
      {declared.length === 0 && (
        <tr>
          <td colSpan={3} className="SpecEditor-preview-calc-empty">
            {t("limsResultCalcNoInputs")}
          </td>
        </tr>
      )}
      {declared.map((inp) => {
        const label = inp.label || inp.key
        const labelWithUnit = inp.unit ? `${label} (${inp.unit})` : label
        // SPEC-023: a parameter-sourced input is never typed — it shows the value the engine
        // resolved from the referenced parameter (or blank while that parameter has none).
        if (inp.source === "parameter") {
          const resolved = judged && judged.resolvedInputs ? judged.resolvedInputs[inp.key] : undefined
          return (
            <tr key={inp.key}>
              <th className="SpecEditor-preview-label">
                {labelWithUnit}
                <span className="SpecEditor-preview-calc-auto">
                  {" "}
                  {t("limsResultCalcFromParameter", { name: sourceNameOf(inp.parameterId) })}
                </span>
              </th>
              <td className="SpecEditor-preview-value" colSpan={2}>
                <Text
                  value={resolved === undefined || resolved === null ? "" : String(resolved)}
                  onChange={() => {}}
                  disabled
                />
              </td>
            </tr>
          )
        }
        const autoFilled = inp.source && inp.source !== "manual"
        if (inp.key === aggregateKey) {
          return (
            <tr key={inp.key}>
              <td colSpan={3}>
                <AggregateInput
                  label={labelWithUnit}
                  values={Array.isArray(inputValues[inp.key]) ? inputValues[inp.key] : []}
                  perValueChecks={judged && judged.perValueChecks}
                  onChange={(list) => onAggregateChange(inp.key, list)}
                  t={t}
                />
              </td>
            </tr>
          )
        }
        return (
          <tr key={inp.key}>
            <th className="SpecEditor-preview-label">
              {labelWithUnit}
              {autoFilled && <span className="SpecEditor-preview-calc-auto"> {t("limsResultCalcAutoFilled")}</span>}
            </th>
            <td className="SpecEditor-preview-value" colSpan={2}>
              <Text
                value={inputValues[inp.key] === undefined || inputValues[inp.key] === null ? "" : String(inputValues[inp.key])}
                onChange={(v) => onScalarChange(inp.key, v)}
                disabled={autoFilled}
              />
            </td>
          </tr>
        )
      })}
    </>
  )
}

// Aggregate (n-value) list editor — one row per individual value, with the engine's ±tolerance
// ✓/✗ when an individual limit applies. Mirrors ResultEntryPanel.AggregateInput.
function AggregateInput({ label, values, perValueChecks, onChange, t }) {
  const setAt = (idx, v) => onChange(values.map((x, i) => (i === idx ? v : x)))
  const addRow = () => onChange([...values, ""])
  const removeRow = (idx) => onChange(values.filter((_x, i) => i !== idx))
  return (
    <div className="SpecEditor-preview-aggregate">
      <span className="SpecEditor-preview-calc-label">{label}</span>
      {values.map((v, idx) => {
        const check = Array.isArray(perValueChecks) ? perValueChecks[idx] : null
        return (
          <div className="SpecEditor-preview-agg-row" key={idx}>
            <Text value={v === null || v === undefined ? "" : String(v)} onChange={(nv) => setAt(idx, nv)} />
            {check && (
              <span
                className={"SpecEditor-preview-agg-check " + (check.withinTolerance ? "is-within" : "is-out")}
                title={check.withinTolerance ? t("limsResultCalcWithinTol") : t("limsResultCalcOutOfTol")}
              >
                {check.withinTolerance ? "✓" : "✗"}
              </span>
            )}
            <button
              type="button"
              className="SpecEditor-preview-agg-remove"
              title={t("limsResultCalcRemoveValue")}
              onClick={() => removeRow(idx)}
            >
              <FiTrash2 />
            </button>
          </div>
        )
      })}
      <button type="button" className="SpecEditor-preview-agg-add" onClick={addRow}>
        <FiPlus /> {t("limsResultCalcAddValue")}
      </button>
    </div>
  )
}

// Human-readable acceptance criterion for a spec parameter (read-only hint). Mirrors
// ResultEntryPanel.criterionText.
function criterionText(param, t) {
  const { criterionType, min, max } = param
  switch (criterionType) {
    case "min":
      return `≥ ${formatBound(min)}`
    case "max":
      return `≤ ${formatBound(max)}`
    case "range":
      return `${formatBound(min)} – ${formatBound(max)}`
    default:
      return t("limsResultNoCriterion")
  }
}

function formatBound(v) {
  return v === null || v === undefined || v === "" ? "—" : String(v)
}

// PASS / FAIL / "—" (not yet judged) badge. `pass` is true | false | null. Reuses the
// ResultEntryPanel flag class names so the preview looks identical to the real panel.
function PassFlag({ pass, t }) {
  if (pass === null || pass === undefined) {
    return <span className="ResultEntryPanel-flag is-pending">—</span>
  }
  return (
    <span className={"ResultEntryPanel-flag " + (pass ? "is-pass" : "is-fail")}>
      {pass ? t("limsResultPass") : t("limsResultFail")}
    </span>
  )
}
